import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
  rounded?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({
  className = '', 
  width = 'w-full', 
  height = 'h-4', 
  rounded = 'rounded' 
}) => {
  return (
    <div 
      className={`${width} ${height} ${rounded} bg-slate-200 animate-pulse ${className}`}
    />
  );
};

// Skeleton específico para StatsCard
export const StatsCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <Skeleton className="mb-2" height="h-4" width="w-24" />
          <Skeleton className="mb-1" height="h-8" width="w-16" />
        </div>
        <Skeleton className="w-12 h-12 rounded-xl" />
      </div>
    </div>
  );
};

// Skeleton específico para ContractCard
export const ContractCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <Skeleton className="mb-2" height="h-4" width="w-32" />
          <div className="flex items-center gap-2 mb-2">
            <Skeleton className="w-5 h-5 rounded-full" />
            <Skeleton className="w-24" height="h-4" />
          </div>
        </div>
        <Skeleton className="w-20 h-6 rounded-full" />
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Skeleton className="w-5 h-5 rounded-full" />
            <Skeleton className="w-24" height="h-4" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="w-5 h-5 rounded-full" />
            <Skeleton className="w-20" height="h-4" />
          </div>
        </div>
        
        <div className="flex items-start gap-2">
          <Skeleton className="w-5 h-5 rounded-full mt-0.5" />
          <Skeleton className="w-32" height="h-4" />
        </div>
        
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <Skeleton className="w-5 h-5 rounded-full" />
          <Skeleton className="w-20" height="h-4" />
        </div>
      </div>
      
      <div className="px-4 py-3 bg-slate-50/50 border-t border-slate-100 mt-4">
        <div className="flex items-center justify-between">
          <Skeleton className="w-24 h-8 rounded-lg" />
          <div className="flex items-center gap-1">
            <Skeleton className="w-8 h-8 rounded-lg" />
            <Skeleton className="w-8 h-8 rounded-lg" />
            <Skeleton className="w-8 h-8 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Skeleton para grid de cards
export const CardsGridSkeleton: React.FC = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
      {Array.from({ length: 10 }).map((_, index) => (
        <ContractCardSkeleton key={index} />
      ))}
    </div>
  );
};

// Skeleton para lista de dados
export const DataTableSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="w-32 h-6" />
          <Skeleton className="w-24 h-10 rounded-lg" />
        </div>
        
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-b-0">
              <Skeleton className="w-4 h-4 rounded" />
              <Skeleton className="w-32 h-4" />
              <Skeleton className="w-24 h-4" />
              <Skeleton className="w-20 h-4" />
              <Skeleton className="w-16 h-4" />
              <Skeleton className="w-20 h-6 rounded-full" />
              <div className="flex items-center gap-2 ml-auto">
                <Skeleton className="w-8 h-8 rounded-lg" />
                <Skeleton className="w-8 h-8 rounded-lg" />
                <Skeleton className="w-8 h-8 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Skeletons para a página de visualização de contratos
export const ContractViewSkeleton: React.FC = () => {
  return (
    <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 py-2 overflow-x-hidden">
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-1 lg:gap-2 items-start">
        {/* Contract Info - 5 colunas */}
        <div className="xl:col-span-5 space-y-2 order-1 xl:order-1">
          {/* Contract Header Skeleton */}
          <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-slate-500 to-slate-600 text-white p-2">
              <Skeleton className="w-32 h-4 mb-2" />
              <Skeleton className="w-24 h-3" />
            </div>
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Skeleton className="w-5 h-5 rounded-full" />
                <Skeleton className="w-20 h-4" />
              </div>
            </div>
          </div>

          {/* Info Grid Skeleton */}
          <div className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* Personal Info Card Skeleton */}
              <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-slate-500 to-slate-600 text-white p-2">
                  <Skeleton className="w-24 h-3 mb-1" />
                  <Skeleton className="w-20 h-2" />
                </div>
                <div className="p-2 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-16 h-3" />
                    <Skeleton className="w-24 h-3" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-16 h-3" />
                    <Skeleton className="w-20 h-3" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-16 h-3" />
                    <Skeleton className="w-24 h-3" />
                  </div>
                </div>
              </div>

              {/* Event Details Card Skeleton */}
              <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-slate-500 to-slate-600 text-white p-2">
                  <Skeleton className="w-24 h-3 mb-1" />
                  <Skeleton className="w-20 h-2" />
                </div>
                <div className="p-2 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-16 h-3" />
                    <Skeleton className="w-20 h-3" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-16 h-3" />
                    <Skeleton className="w-16 h-3" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-16 h-3" />
                    <Skeleton className="w-16 h-3" />
                  </div>
                </div>
              </div>

              {/* Location Card Skeleton */}
              <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-slate-500 to-slate-600 text-white p-2">
                  <Skeleton className="w-20 h-3 mb-1" />
                  <Skeleton className="w-24 h-2" />
                </div>
                <div className="p-2 space-y-2">
                  <div className="space-y-1.5">
                    <div className="text-xs">
                      <Skeleton className="w-16 h-3 mb-1" />
                      <Skeleton className="w-28 h-3" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      <Skeleton className="w-20 h-3" />
                      <Skeleton className="w-16 h-3" />
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-1 pt-1">
                    <Skeleton className="w-full h-6 rounded" />
                    <Skeleton className="w-full h-6 rounded" />
                    <Skeleton className="w-full h-6 rounded" />
                  </div>
                  <Skeleton className="w-full h-32 rounded-lg" />
                </div>
              </div>

              {/* Package Payment Card Skeleton */}
              <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-slate-500 to-slate-600 text-white p-2">
                  <Skeleton className="w-24 h-3 mb-1" />
                  <Skeleton className="w-20 h-2" />
                </div>
                <div className="p-2 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-16 h-3" />
                    <Skeleton className="w-20 h-3" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-16 h-3" />
                    <Skeleton className="w-16 h-3" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-16 h-3" />
                    <Skeleton className="w-16 h-6 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - 3 colunas */}
        <div className="xl:col-span-3 xl:space-y-2 space-y-1 order-2 xl:order-3">
          {/* Quick Actions Skeleton */}
          <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-slate-500 to-slate-600 text-white p-2">
              <Skeleton className="w-20 h-3 mb-1" />
              <Skeleton className="w-16 h-2" />
            </div>
            <div className="p-3">
              <div className="grid grid-cols-2 gap-2">
                <Skeleton className="w-full h-8 rounded-lg" />
                <Skeleton className="w-full h-8 rounded-lg" />
              </div>
            </div>
          </div>

          {/* Summary Skeleton */}
          <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-slate-500 to-slate-600 text-white p-2">
              <Skeleton className="w-16 h-3 mb-1" />
              <Skeleton className="w-20 h-2" />
            </div>
            <div className="p-3">
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <Skeleton className="w-16 h-3" />
                  <Skeleton className="w-20 h-5 rounded-full" />
                </div>
                <div className="flex justify-between items-center">
                  <Skeleton className="w-20 h-3" />
                  <Skeleton className="w-16 h-5 rounded-full" />
                </div>
                <div className="flex justify-between items-center">
                  <Skeleton className="w-12 h-3" />
                  <Skeleton className="w-16 h-3" />
                </div>
                <div className="flex justify-between items-center">
                  <Skeleton className="w-20 h-3" />
                  <Skeleton className="w-12 h-3" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contract Pages - 4 colunas */}
        <div className="xl:col-span-4 space-y-2 overflow-x-hidden order-3 xl:order-2">
          {/* Navigation Bar Skeleton */}
          <div className="bg-slate-50 rounded-lg p-2 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <Skeleton className="w-6 h-6 rounded" />
              <Skeleton className="w-6 h-6 rounded" />
              <Skeleton className="w-6 h-6 rounded" />
              <Skeleton className="w-6 h-6 rounded" />
              <Skeleton className="w-6 h-6 rounded" />
            </div>
          </div>

          {/* Contract Page Skeleton */}
          <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
            <div className="relative w-full flex justify-center overflow-x-auto">
              <div className="flex-shrink-0 xl:w-auto w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl">
                <div className="w-full h-full scale-75 sm:scale-90 xl:scale-100 origin-top-left">
                  <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden relative" 
                       style={{ width: '210mm', height: '297mm' }}>
                    {/* Header Image Skeleton */}
                    <Skeleton className="w-full h-16" />
                    
                    {/* Content Skeleton */}
                    <div className="p-3">
                      <div className="space-y-3">
                        <Skeleton className="w-full h-6" />
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-3/4 h-4" />
                        <Skeleton className="w-full h-6" />
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-5/6 h-4" />
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-4/5 h-4" />
                        <Skeleton className="w-full h-6" />
                        <Skeleton className="w-full h-4" />
                        <Skeleton className="w-3/4 h-4" />
                      </div>
                    </div>
                    
                    {/* Footer Image Skeleton */}
                    <div className="absolute bottom-0 left-0 w-full">
                      <Skeleton className="w-full h-16" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Skeleton;
